import { useEffect, useState, FC, useMemo } from 'react';
import { Button, Card, Menu, Row, Typography } from 'antd';
import i18n from 'i18next';
import classNames from 'classnames';

import { EmptyComponent, Skeleton } from 'componets/ui';
import { DropDownComponent } from 'componets/common';
import { InvestmentStatisticRow } from './InvestmentStatisticRow';
import { InvestmentsModal } from './InvestmentsModal';

import { getInvestmentStatistic } from 'features/overview/actions';

import { useAppSelector, useTranslate, useAppDispatch } from 'hooks';

import Utils from 'utils/utils';

import { RequestStatus } from 'utils/constants';

import { OverviewBlockProps } from 'models';

import styles from './index.module.scss';

const TYPE_OPTIONS = [
	{ value: 'investment_types', label: i18n.t('investmentTypesText') },
	{ value: 'countries', label: i18n.t('countriesText') },
	{ value: 'interest-rates', label: i18n.t('interestRatesText') },
	{ value: 'investment_statuses', label: i18n.t('investmentStatusesText') },
];

export const Investments: FC<OverviewBlockProps> = ({ isLoading }) => {
	const dispatch = useAppDispatch();

	const [isOpen, setIsOpen] = useState(false);
	const [type, setType] = useState('investment_types');

	const { investmentsText, viewAllText } = useTranslate(['investmentsText', 'viewAllText']);

	const { data, status } = useAppSelector((state) => state.overview.investments);

	useEffect(() => {
		dispatch(getInvestmentStatistic(type));
	}, [dispatch, type]);

	const filtered = useMemo(() => data.filter((item) => item.part), [data]);

	const values = useMemo(
		() =>
			type === 'investment_statuses'
				? filtered.filter((item, i) => {
						if (i <= 2) {
							return item;
						} else {
							const length = filtered.length;

							if (
								(i === 3 && length === 5 && +filtered[4].amount === 0) ||
								(i === 3 && length === 4)
							) {
								return +item.amount > 0;
							} else if (length === 5 && i === 3 && +filtered[4].amount > 0) {
								return item.amount;
							}

							if (i === 4) {
								return +item.amount > 0;
							}
						}
				  })
				: filtered
						.filter((item) => +item.amount > 0)
						.sort((a, b) => Number(b.part) - Number(a.part)),
		[filtered, type],
	);

	const transformedValues = useMemo(() => Utils.transformValues(values), [values]);

	const label = useMemo(() => TYPE_OPTIONS.find((item) => item.value === type)?.label, [type]);

	return (
		<Card bordered={false} className={styles.root}>
			<Skeleton loading={isLoading}>
				<>
					<Typography.Title level={4}>{investmentsText}</Typography.Title>

					<div className="chartType">
						<DropDownComponent
							placement="bottom"
							dropdownRender={() => (
								<Menu className="dropdown-wrapper">
									{TYPE_OPTIONS.map((item) => {
										return (
											<Menu.Item
												className={classNames('documentButton', 'documentButton--investment', {
													active: item.value === type,
												})}
												key={item.value}
												onClick={() => {
													setType(item.value);
												}}
											>
												{item.label}
											</Menu.Item>
										);
									})}
								</Menu>
							)}
							label={label || ''}
						/>
					</div>

					<Skeleton loading={status === RequestStatus.PENDING}>
						<>
							{values.length === 0 ? (
								<EmptyComponent />
							) : (
								<Row gutter={[0, 28]}>
									{transformedValues.map((el, i: number) => (
										<InvestmentStatisticRow key={i} el={el} i={i} />
									))}

									{values.length > 4 && (
										<Button className="white-btn" onClick={() => setIsOpen(true)}>
											{viewAllText}
										</Button>
									)}

									{isOpen && (
										<InvestmentsModal
											title={TYPE_OPTIONS.find((item) => item.value === type)?.label}
											isOpen={isOpen}
											onClose={() => setIsOpen(false)}
											values={values}
										/>
									)}
								</Row>
							)}
						</>
					</Skeleton>
				</>
			</Skeleton>
		</Card>
	);
};
